import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import Partners from "@/pages/Partners";
import Requests from "@/pages/Requests";
import Orders from "@/pages/Orders";
import Feedback from "@/pages/Feedback";
import Ideas from "@/pages/Ideas";
import ReworkScrap from "@/pages/ReworkScrap";
import Tools from "@/pages/Tools";
import Network from "@/pages/Network";
import Shipping from "@/pages/Shipping";
import Logistics from "@/pages/Logistics";
import Insurance from "@/pages/Insurance";
import AgentDesk from "@/pages/AgentDesk";
import Finance from "@/pages/Finance";
import FinanceDesk from "@/pages/FinanceDesk";
import Privacy from "@/pages/Privacy";
import LiveFeed from "@/pages/LiveFeed";
import Legal from "@/pages/Legal";
import AI from "@/pages/AI";
import Marketplace from "@/pages/Marketplace";
import PaymentResult from "@/pages/PaymentResult";
import Payments from "@/pages/Payments";
import PayRequest from "@/pages/PayRequest";
import Stories from "@/pages/Stories";
import StoryPublic from "@/pages/StoryPublic";
import FeedPublic from "@/pages/FeedPublic";
import Jobs from "@/pages/Jobs";
import Profile from "@/pages/Profile";
import Services from "@/pages/Services";
import Help from "@/pages/Help";
import Safety from "@/pages/Safety";
import Tariffs from "@/pages/Tariffs";
import Parcels from "@/pages/Parcels";
import TrackPublic from "@/pages/TrackPublic";
import Videos from "@/pages/Videos";
import SafetyPublic from "@/pages/SafetyPublic";
import PublicWall from "@/pages/PublicWall";
import Home from "@/pages/Home";
import Solution from "@/pages/Solution";
import Guide, { Learn } from "@/pages/Guide";
import FreightCalculator from "@/pages/FreightCalculator";
import FinanceChecker from "@/pages/FinanceChecker";
import JobPublic from "@/pages/JobPublic";
import LegalAdvisory from "@/pages/LegalAdvisory";
import LegalGuide from "@/pages/LegalGuide";
import Members from "@/pages/Members";
import News from "@/pages/News";
import FreightTime from "@/pages/FreightTime";
import Warehouse from "@/pages/Warehouse";
import Markets from "@/pages/Markets";
import Commodities from "@/pages/Commodities";
import MarketsPublic from "@/pages/MarketsPublic";
import ForgotPassword from "@/pages/ForgotPassword";
import Fashion from "@/pages/Fashion";
import Cafeteria from "@/pages/Cafeteria";
import Ads from "@/pages/Ads";
import Catalogue from "@/pages/Catalogue";
import CataloguePublic, { CatalogueEntryPublic } from "@/pages/CataloguePublic";
import RestSleep from "@/pages/RestSleep";
import RestSleepPublic, { RestItemPublic, RestRulePublic, RestCampaignPublic } from "@/pages/RestSleepPublic";
import WellbeingPublic from "@/pages/WellbeingPublic";
import Motels from "@/pages/Motels";
import MotelsPublic, { MotelPublic } from "@/pages/MotelsPublic";
import Refreshments from "@/pages/Refreshments";
import RefreshmentsPublic, { RefreshmentPublic } from "@/pages/RefreshmentsPublic";
import AdsPublic, { AdPublic } from "@/pages/AdsPublic";
import CastingPublic from "@/pages/CastingPublic";
import FashionPublic from "@/pages/FashionPublic";
import FoodCourtPublic, { FoodItemPublic } from "@/pages/FoodCourtPublic";
import Iphone from "@/pages/Iphone";
import Android from "@/pages/Android";
import StretchingPublic from "@/pages/StretchingPublic";
import Teamwork from "@/pages/Teamwork";
import TeamworkPublic from "@/pages/TeamworkPublic";
import Notices, { NoticePrint } from "@/pages/Notices";
import Accessibility from "@/pages/Accessibility";
import Pantry from "@/pages/Pantry";
import Storage from "@/pages/Storage";
import Companion from "@/pages/Companion";
import CompanionPublic from "@/pages/CompanionPublic";
import Housing from "@/pages/Housing";
import HousingPublic from "@/pages/HousingPublic";
import Farmers from "@/pages/Farmers";
import Fridge from "@/pages/Fridge";
import FridgePublic from "@/pages/FridgePublic";
import CarCare from "@/pages/CarCare";
import CarCarePublic from "@/pages/CarCarePublic";
import PriceLock from "@/pages/PriceLock";
import PriceLockPublic from "@/pages/PriceLockPublic";
import English from "@/pages/English";
import PartsGuard from "@/pages/PartsGuard";
import PartsGuardPublic from "@/pages/PartsGuardPublic";
import EnglishPublic from "@/pages/EnglishPublic";
import FarmersPublic from "@/pages/FarmersPublic";
import PantryPublic from "@/pages/PantryPublic";
import { AccessProvider } from "@/context/AccessContext";
import { AccessButton } from "@/components/AccessButton";
import { AddToHome } from "@/components/AddToHome";
import { InstallAndroid } from "@/components/InstallAndroid";
import FashionItemPublic from "@/pages/FashionItemPublic";
import ResetPassword from "@/pages/ResetPassword";
import WorldMarketsPublic from "@/pages/WorldMarketsPublic";
import NewsPublic from "@/pages/NewsPublic";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading)
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">
        Loading workspace…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && location.pathname !== "/privacy")
    return <Navigate to="/privacy" replace state={{ forcePassword: true }} />;
  return (
    <CurrencyProvider>
      <Layout>{children}</Layout>
    </CurrencyProvider>
  );
};

// /news serves both: the workspace page when signed in, the public page otherwise.
const NewsRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <NewsPublic />;
  return (
    <CurrencyProvider>
      <Layout><News /></Layout>
    </CurrencyProvider>
  );
};

const MarketsRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <MarketsPublic />;
  return (
    <CurrencyProvider>
      <Layout><Markets /></Layout>
    </CurrencyProvider>
  );
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user)
    return (
      <Navigate
        to={user.must_change_password ? "/privacy" : "/dashboard"}
        replace
        state={user.must_change_password ? { forcePassword: true } : undefined}
      />
    );
  return children;
};

const PAGES = [
  ["/dashboard", Dashboard],
  ["/inventory", Inventory],
  ["/warehouse", Warehouse],
  ["/commodities", Commodities],
  ["/fashion", Fashion],
  ["/cafeteria", Cafeteria],
  ["/advertising", Ads],
  ["/catalogue-board", Catalogue],
  ["/rest-sleep", RestSleep],
  ["/teamwork-desk", Teamwork],
  ["/pantry-desk", Pantry],
  ["/space-vans", Storage],
  ["/companion", Companion],
  ["/housing-desk", Housing],
  ["/farmers-desk", Farmers],
  ["/fridge", Fridge],
  ["/car-care-desk", CarCare],
  ["/price-lock-desk", PriceLock],
  ["/english-desk", English],
  ["/parts-guard-desk", PartsGuard],
  ["/motels-board", Motels],
  ["/drinks-supplies", Refreshments],
  ["/partners", Partners],
  ["/requests", Requests],
  ["/orders", Orders],
  ["/feedback", Feedback],
  ["/ideas", Ideas],
  ["/shipping", Shipping],
  ["/logistics", Logistics],
  ["/freight-time", FreightTime],
  ["/insurance", Insurance],
  ["/agent-desk", AgentDesk],
  ["/finance", Finance],
  ["/finance-desk", FinanceDesk],
  ["/privacy", Privacy],
  ["/live-feed", LiveFeed],
  ["/jobs", Jobs],
  ["/advocates", LegalAdvisory],
  ["/profile", Profile],
  ["/services", Services],
  ["/help", Help],
  ["/safety", Safety],
  ["/tariffs", Tariffs],
  ["/parcels", Parcels],
  ["/ai", AI],
  ["/payments", Payments],
  ["/stories", Stories],
  ["/rework-scrap", ReworkScrap],
  ["/tools", Tools],
  ["/network", Network],
  ["/marketplace", Marketplace],
  ["/members", Members],
];

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AccessProvider>
          <a href="#main-content" className="skip-link" data-testid="skip-link">Skip to content</a>
          <Toaster position="top-right" />
          <AppRoutes />
          <AddToHome />
          <InstallAndroid />
          <AccessButton />
          </AccessProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Emergent Google auth returns to #session_id=... — handle it before any route guard runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            {PAGES.map(([path, C]) => (
              <Route key={path} path={path} element={<Protected><C /></Protected>} />
            ))}
            <Route path="/pay/:token" element={<PayRequest />} />
            <Route path="/s/:token" element={<StoryPublic />} />
            <Route path="/p/:token" element={<FeedPublic />} />
            <Route path="/jobs/:token" element={<JobPublic />} />
            <Route path="/safety/:token" element={<SafetyPublic />} />
            <Route path="/feed" element={<PublicWall />} />
            <Route path="/iphone" element={<Iphone />} />
            <Route path="/android" element={<Android />} />
            <Route path="/stretching" element={<StretchingPublic />} />
            <Route path="/teamwork" element={<TeamworkPublic />} />
            <Route path="/notices" element={<Notices />} />
            <Route path="/notices/:noticeId/print" element={<NoticePrint />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/pantry" element={<PantryPublic />} />
            <Route path="/rides" element={<CompanionPublic />} />
            <Route path="/housing" element={<HousingPublic />} />
            <Route path="/farmers-market" element={<FarmersPublic />} />
            <Route path="/workplace-fridge" element={<FridgePublic />} />
            <Route path="/car-care" element={<CarCarePublic />} />
            <Route path="/price-lock" element={<PriceLockPublic />} />
            <Route path="/english" element={<EnglishPublic />} />            <Route path="/parts-guard" element={<PartsGuardPublic />} />            <Route path="/track/:number" element={<TrackPublic />} />
            <Route path="/videos" element={<Videos />} />
            <Route path="/news" element={<NewsRoute />} />
            <Route path="/markets" element={<MarketsRoute />} />
            <Route path="/world-markets" element={<WorldMarketsPublic />} />
            <Route path="/casting/:token" element={<CastingPublic />} />
            <Route path="/fashion-trends" element={<FashionPublic />} />
            <Route path="/food-court" element={<FoodCourtPublic />} />
            <Route path="/ads" element={<AdsPublic />} />
            <Route path="/catalogue" element={<CataloguePublic />} />
            <Route path="/catalogue/:kind/:id" element={<CatalogueEntryPublic />} />
            <Route path="/rest-sleep-board" element={<RestSleepPublic />} />
            <Route path="/rest-sleep-rule" element={<RestRulePublic />} />
            <Route path="/rest-sleep-campaign" element={<RestCampaignPublic />} />
            <Route path="/wellbeing" element={<WellbeingPublic />} />
            <Route path="/motels" element={<MotelsPublic />} />
            <Route path="/motels/:id" element={<MotelPublic />} />
            <Route path="/refreshments" element={<RefreshmentsPublic />} />
            <Route path="/refreshments/:id" element={<RefreshmentPublic />} />
            <Route path="/rest-sleep-board/:id" element={<RestItemPublic />} />
            <Route path="/ads/:token" element={<AdPublic />} />
            <Route path="/food-court/:id" element={<FoodItemPublic />} />
            <Route path="/fashion-trends/:kind/:id" element={<FashionItemPublic />} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<Home />} />
            <Route path="/solutions/:slug" element={<Solution />} />
            <Route path="/learn" element={<Learn />} />
            <Route path="/learn/:slug" element={<Guide />} />
            <Route path="/tools/freight-calculator" element={<FreightCalculator />} />
            <Route path="/tools/finance-checker" element={<FinanceChecker />} />
            <Route path="/guides/:slug" element={<LegalGuide />} />
            <Route path="/legal/:section" element={<Legal />} />

            <Route path="/payment/success" element={<Protected><PaymentResult outcome="success" /></Protected>} />
            <Route path="/payment/cancel" element={<Protected><PaymentResult outcome="cancel" /></Protected>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
  );
}

export default App;
